import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import api from '../../../api';
import { Button, ButtonStyle } from '../../Utils';
import ConfigSnippetPanel from './ConfigSnippetPanel';
import PatCreateModal from './PatCreateModal';

import * as sShared from '../SettingsShared.module.scss';
import * as s from './AccessTokensSettings.module.scss';

function formatRelative(t, isoString) {
  if (!isoString) return t('common.never');
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return t('common.never');
  const deltaSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (deltaSec < 60) return t('common.justNow');
  if (deltaSec < 3600) return `${Math.round(deltaSec / 60)}m`;
  if (deltaSec < 86400) return `${Math.round(deltaSec / 3600)}h`;
  return `${Math.round(deltaSec / 86400)}d`;
}

const INTRO_FALLBACK =
  'Use these tokens to connect Claude Desktop, Claude Code, Cursor, and other MCP-compatible AI hosts to your boards. Each token is shown only once when generated — copy it somewhere safe.';

const AccessTokensSettings = React.memo(() => {
  const [t] = useTranslation();
  const [tokens, setTokens] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateOpen, setCreateOpen] = useState(false);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const body = await api.listPersonalAccessTokens();
      setTokens(Array.isArray(body && body.items) ? body.items : []);
    } catch (err) {
      setError((err && err.message) || t('common.somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = useCallback(
    async ({ name, scopes }) => {
      const body = await api.createPersonalAccessToken({ name, scopes });
      await refresh();
      return body && body.item;
    },
    [refresh],
  );

  const handleRename = useCallback(
    async (id) => {
      // eslint-disable-next-line no-alert
      const newName = window.prompt(t('common.renameAccessToken'));
      if (!newName || !newName.trim()) return;
      try {
        await api.updatePersonalAccessToken(id, { name: newName.trim() });
        await refresh();
      } catch (err) {
        setError((err && err.message) || t('common.somethingWentWrong'));
      }
    },
    [refresh, t],
  );

  const handleRevoke = useCallback(
    async (token) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(t('common.areYouSureRevokeAccessToken', { name: token.name || 'this token' }))) {
        return;
      }
      try {
        await api.deletePersonalAccessToken(token.id);
        await refresh();
      } catch (err) {
        setError((err && err.message) || t('common.somethingWentWrong'));
      }
    },
    [refresh, t],
  );

  return (
    <div className={sShared.wrapper}>
      <div className={sShared.header}>
        <h2 className={sShared.headerText}>{t('common.accessTokens', 'Personal Access Tokens')}</h2>
      </div>

      <div className={s.intro}>{t('common.accessTokensIntro', INTRO_FALLBACK)}</div>

      {error && <div className={s.error}>{error}</div>}

      <div className={s.actions}>
        <Button style={ButtonStyle.DefaultBorder} content={t('common.generateToken', 'Generate token')} onClick={() => setCreateOpen(true)} />
      </div>

      {isLoading && <div className={s.loading}>{t('common.loading')}…</div>}

      {!isLoading && tokens.length === 0 && <div className={s.empty}>{t('common.noAccessTokensYet', 'No tokens yet. Generate one to get started.')}</div>}

      {!isLoading && tokens.length > 0 && (
        <ul className={s.list}>
          {tokens.map((token) => (
            <li key={token.id} className={s.row}>
              <div className={s.rowMain}>
                <div className={s.name}>
                  {token.name || t('common.unnamedToken', 'Unnamed token')}
                  <span className={s.scopeBadge} title={(token.scopes || []).join(', ')}>
                    {(token.scopes || []).includes('write') ? 'READ+WRITE' : 'READ'}
                  </span>
                </div>
                <div className={s.token}>{token.maskedToken}</div>
                <div className={s.meta}>
                  {t('common.created', 'Created')} {new Date(token.createdAt).toLocaleDateString()} · {t('common.lastUsed', 'Last used')} {formatRelative(t, token.lastUsedAt)}
                </div>
              </div>
              <div className={s.rowActions}>
                <Button style={ButtonStyle.NoBackground} content={t('action.rename', 'Rename')} onClick={() => handleRename(token.id)} />
                <Button style={ButtonStyle.NoBackground} content={t('action.revoke', 'Revoke')} onClick={() => handleRevoke(token)} className={s.revoke} />
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfigSnippetPanel />

      {isCreateOpen && <PatCreateModal onClose={() => setCreateOpen(false)} onCreate={handleCreate} />}
    </div>
  );
});

AccessTokensSettings.propTypes = {};

export default AccessTokensSettings;
